import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { useS3Upload } from '../../hooks/useS3Upload/useS3Upload';
import i18next from 'i18next';
import { EntityScope, ExerciseType } from '../../../../shared/utils/enums';
import { Button, Col, Form, Row, Stack } from 'react-bootstrap';
import LexicalEditor from '../textEditor/LexicalEditor';
import { exerciseTypeLabels } from '../../util/types';
import { Typeahead } from 'react-bootstrap-typeahead';
import DropZone from '../DropZone';
import { VideoUploadRequest } from '../../../../shared/requests/videoUpload.request';
import { AudioUploadDto, VideoUploadDto } from '../../../../shared/dtos/upload.dto';
import { AudioUploadRequest } from '../../../../shared/requests/audioUpload.request';
import Toggle from '../Toggle';
import { ExerciseDto } from "../../../../shared/dtos/exercise.dto";
import { ExerciseTagDto } from "../../../../shared/dtos/exerciseTag.dto";
import { ExerciseRequest } from "../../../../shared/requests/exercise.request";
import UploadOverlay, { Upload, UploadStatus } from '../UploadOverlay';

type FormValues = {
  germanTitle: string;
  englishTitle: string;
  germanDescription: string;
  englishDescription: string;
  exerciseType: ExerciseType;
  isTrainingTimeBillable: boolean;
  tags: ExerciseTagDto[];
  horizontalVideo: File | null;
  verticalVideo: File | null;
  horizontalThumbnail: File | null;
  verticalThumbnail: File | null;
  audioFile: File | null;
}

type ExerciseFormProps = {
  initialData: ExerciseDto | null;
  tags: ExerciseTagDto[];
  entityScope: EntityScope;
  isSuperAdmin: boolean;
  onCancel: () => void;
  onSubmit: (exerciseVideo: ExerciseDto) => void;
}

type UploadKey = 'horizontalVideo' | 'verticalVideo' | 'horizontalThumbnail' | 'verticalThumbnail' | 'audioFile';

type PreviewUrls = {
  horizontalVideo: string | null;
  verticalVideo: string | null;
  horizontalThumbnail: string | null;
  verticalThumbnail: string | null;
  audioFile: string | null;
}

const ExerciseForm: FC<ExerciseFormProps> = ({
  initialData,
  tags,
  entityScope,
  isSuperAdmin,
  onCancel,
  onSubmit
}: ExerciseFormProps): ReactElement => {
  const existingUrls = useMemo(() => ({
    horizontalVideo: initialData?.horizontalUrl ?? null,
    verticalVideo: initialData?.verticalUrl ?? null,
    horizontalThumbnail: initialData?.hThumbnail ?? null,
    verticalThumbnail: initialData?.vThumbnail ?? null,
    audioFile: initialData?.mp3File ?? null,
  }), [initialData]);

  const [previewUrls, setPreviewUrls] = useState<PreviewUrls>({
    horizontalVideo: null,
    verticalVideo: null,
    horizontalThumbnail: null,
    verticalThumbnail: null,
    audioFile: null,
  });

  const [uploadStates, setUploadStates] = useState<Record<UploadKey, Upload>>({
    horizontalVideo: { progress: null, status: 'idle' },
    verticalVideo: { progress: null, status: 'idle' },
    horizontalThumbnail: { progress: null, status: 'idle' },
    verticalThumbnail: { progress: null, status: 'idle' },
    audioFile: { progress: null, status: 'idle' },
  });

  const updateUploadState = useCallback((key: UploadKey, progress: number | null, status?: UploadStatus): void => {
    setUploadStates((prev: Record<UploadKey, Upload>) => ({
      ...prev,
      [key]: { progress, status: status ?? prev[key].status }
    }));
  }, []);

  const startUploadFor = (keys: UploadKey[]): void => {
    setUploadStates(((prev:  Record<UploadKey, Upload>) => {
      const next = { ...prev };
      keys.forEach((key: UploadKey) => (next[key] = { progress: 0, status: 'uploading' }));
      return next;
    }));
  };

  const { authFetch } = useAuthFetchFromRestAPI();

  const { uploadFile: uploadHorizontalVideo } = useS3Upload();
  const { uploadFile: uploadVerticalVideo } = useS3Upload();
  const { uploadFile: uploadHorizontalThumbnail } = useS3Upload();
  const { uploadFile: uploadVerticalThumbnail } = useS3Upload();
  const { uploadFile: uploadAudioFile } = useS3Upload();

  const selectedTagsFromInitial = (all: ExerciseTagDto[], ids: string[]): ExerciseTagDto[] => {
    if (!ids.length) {
      return [];
    }

    const idSet: Set<string> = new Set(ids.map((id: string) => id));
    return all.filter((tag: ExerciseTagDto) => idSet.has(tag.id));
  };

  const initialValues: FormValues = {
    germanTitle: initialData?.deName ?? '',
    englishTitle: initialData?.enName ?? '',
    germanDescription: initialData?.deDescription ?? '',
    englishDescription: initialData?.enDescription ?? '',
    exerciseType: initialData?.exerciseType || ExerciseType.STATIC,
    isTrainingTimeBillable: initialData?.isTrainingTimeBillable ?? true,
    tags: selectedTagsFromInitial(tags, initialData?.tagIds ?? []),
    horizontalVideo: null,
    verticalVideo: null,
    horizontalThumbnail: null,
    verticalThumbnail: null,
    audioFile: null,
  };

  const buildValidationSchema = useCallback((isSuperAdmin: boolean) => {
    const requiredMessage: string = i18next.t('generic.required');
    const isRelaxation = (val: unknown): boolean => val === ExerciseType.RELAXATION;
    const fileRule = (hasExisting: boolean) => Yup.mixed<File>().nullable().when('exerciseType', {
      is: isRelaxation,
      then: schema => schema.nullable(),
      otherwise: schema => (isSuperAdmin && !hasExisting ? schema.required(requiredMessage) : schema.nullable())
    });

    return Yup.object().shape({
      germanTitle: Yup.string().required(requiredMessage),
      englishTitle: Yup.string().required(requiredMessage),
      germanDescription: Yup.string().required(requiredMessage),
      englishDescription: Yup.string().required(requiredMessage),
      exerciseType: Yup.mixed<ExerciseType>().oneOf(Object.values(ExerciseType)).required(requiredMessage),
      tags: Yup.array().of(Yup.object({ id: Yup.string().required() })).min(1, requiredMessage).required().defined(),

      horizontalVideo: fileRule(!!existingUrls.horizontalVideo),
      verticalVideo: fileRule(!!existingUrls.verticalVideo),
      horizontalThumbnail: fileRule(!!existingUrls.horizontalThumbnail),
      verticalThumbnail: fileRule(!!existingUrls.verticalThumbnail),

      audioFile: Yup.mixed<File>().nullable().when('exerciseType', {
        is: isRelaxation,
        then: schema => (isSuperAdmin && !existingUrls.audioFile ? schema.required(requiredMessage) : schema.nullable()),
        otherwise: schema => schema.nullable(),
      })
    });
  }, [isSuperAdmin, existingUrls]);

  const validationSchema = useMemo(() => buildValidationSchema(isSuperAdmin), [buildValidationSchema, isSuperAdmin]);

  const buildPresignedInput = (values: FormValues): VideoUploadRequest => {
    const input: VideoUploadRequest = { scope: entityScope };
    
    if (values.horizontalVideo) {
      input.horizontalVideoContentType = values.horizontalVideo.type;
    }

    if (values.verticalVideo) {
      input.verticalVideoContentType = values.verticalVideo.type;
    }

    if (values.horizontalThumbnail) {
      input.horizontalThumbnailContentType = values.horizontalThumbnail.type;
    }

    if (values.verticalThumbnail) {
      input.verticalThumbnailContentType = values.verticalThumbnail.type;
    }

    return input;
  };

  const buildPresignedInputForRelaxationExercise = (values: FormValues): AudioUploadRequest | null => {
    if (!values.audioFile) {
      return null;
    }
    
    return { 
      scope: entityScope,
      audioContentType: values.audioFile.type
    };
  };

  const createPresignedUrlForRelaxationExercise = async (values: FormValues): Promise<AudioUploadDto | null> => {
    const input: AudioUploadRequest | null = buildPresignedInputForRelaxationExercise(values);

    if (!input?.audioContentType) {
      return null;
    }

    return authFetch('/api/v1/file-upload/audio', {
      method: 'POST',
      body: input,
    });
  };

  const createPresignedUrls = async (values: FormValues): Promise<VideoUploadDto | null> => {
    const input: VideoUploadRequest = buildPresignedInput(values);

    const hasAny: boolean =
      !!input.verticalThumbnailContentType ||
      !!input.horizontalThumbnailContentType ||
      !!input.horizontalVideoContentType ||
      !!input.verticalVideoContentType;

    if (!hasAny) {
      return null;
    }

    return authFetch('/api/v1/file-upload/video', {
      method: 'POST',
      body: input,
    });
  };

  const uploadSelectedVideoFiles = async (urls: VideoUploadDto, values: FormValues): Promise<boolean> => {
    const tasks: Promise<void>[] = [];

    if (urls.horizontalVideo && values.horizontalVideo) {
      startUploadFor(['horizontalVideo']);
      tasks.push(uploadHorizontalVideo(
        urls.horizontalVideo.uploadUrl,
        values.horizontalVideo,
        (progress: number) => updateUploadState('horizontalVideo', progress))
      );
    }

    if (urls.verticalVideo && values.verticalVideo) {
      startUploadFor(['verticalVideo']);
      tasks.push(uploadVerticalVideo(
        urls.verticalVideo.uploadUrl,
        values.verticalVideo,
        (progress: number) => updateUploadState('verticalVideo', progress))
      );
    }

    if (urls.horizontalThumbnail && values.horizontalThumbnail) {
      startUploadFor(['horizontalThumbnail']);
      tasks.push(uploadHorizontalThumbnail(
        urls.horizontalThumbnail.uploadUrl,
        values.horizontalThumbnail,
        (progress: number) => updateUploadState('horizontalThumbnail', progress))
      );
    }

    if (urls.verticalThumbnail && values.verticalThumbnail) {
      startUploadFor(['verticalThumbnail']);
      tasks.push(uploadVerticalThumbnail(
        urls.verticalThumbnail.uploadUrl,
        values.verticalThumbnail,
        (progress: number) => updateUploadState('verticalThumbnail', progress))
      );
    }

    const results = await Promise.allSettled(tasks);

    const ran: UploadKey[] = [];
    if (urls.horizontalVideo && values.horizontalVideo) {
      ran.push('horizontalVideo');
    }

    if (urls.verticalVideo && values.verticalVideo) {
      ran.push('verticalVideo');
    }

    if (urls.horizontalThumbnail && values.horizontalThumbnail) {
      ran.push('horizontalThumbnail');
    }

    if (urls.verticalThumbnail && values.verticalThumbnail) {
      ran.push('verticalThumbnail');
    }

    results.forEach((result, index) => {
      const key: UploadKey = ran[index];
      updateUploadState(key, null, result.status === 'fulfilled' ? 'success' : 'error');
    });

    return results.every(result => result.status === 'fulfilled');
  };

  const uploadSelectedAudioFile = async (urls: AudioUploadDto, values: FormValues): Promise<boolean> => {
    const tasks: Promise<void>[] = [];

    if (urls.audio && values.audioFile) {
      startUploadFor(['audioFile']);
      tasks.push(uploadAudioFile(
        urls.audio.uploadUrl,
        values.audioFile,
        (progress: number) => updateUploadState('audioFile', progress))
      );
    }

    const results = await Promise.allSettled(tasks);

    const ran: UploadKey[] = [];

    if (urls.audio && values.audioFile) {
      ran.push('audioFile');
    }

    results.forEach((result, index) => {
      const key: UploadKey = ran[index];
      updateUploadState(key, null, result.status === 'fulfilled' ? 'success' : 'error');
    });

    return results.every(result => result.status === 'fulfilled');
  };

  const buildInputDto = (urls: VideoUploadDto | null, values: FormValues): ExerciseRequest => {
    return {
      deName: values.germanTitle,
      deDescription: values.germanDescription,
      enName: values.englishTitle,
      enDescription: values.englishDescription,
      isTrainingTimeBillable: values.isTrainingTimeBillable,

      horizontalUrl: (urls?.horizontalVideo?.fileUrl ?? existingUrls.horizontalVideo ?? undefined),
      verticalUrl: (urls?.verticalVideo?.fileUrl ?? existingUrls.verticalVideo ?? undefined),
      hThumbnail: (urls?.horizontalThumbnail?.fileUrl ?? existingUrls.horizontalThumbnail ?? undefined),
      vThumbnail: (urls?.verticalThumbnail?.fileUrl ?? existingUrls.verticalThumbnail ?? undefined),

      exerciseType: values.exerciseType,
      tagIds: values.tags.map((tag: ExerciseTagDto) => tag.id),
    };
  };

  const buildInputDtoForRelaxationExercise = (urls: AudioUploadDto | null, values: FormValues): ExerciseRequest => {

    return {
      deName: values.germanTitle,
      deDescription: values.germanDescription,
      enName: values.englishTitle,
      enDescription: values.englishDescription,
      isTrainingTimeBillable: values.isTrainingTimeBillable,

      mp3File: (urls?.audio?.fileUrl ?? existingUrls.audioFile ?? undefined),
      
      exerciseType: values.exerciseType,
      tagIds: values.tags.map((tag: ExerciseTagDto) => tag.id),
    };
  };

  const submitData = async (payload: ExerciseRequest): Promise<ExerciseDto> => {
    const isUpdate: boolean = !!initialData;
    const baseUrl: string = '/api/v1/exercise';

    if (!isUpdate) {
      const target: string = entityScope === EntityScope.CORE ? `${baseUrl}/core` : `${baseUrl}/tenant`;
      return authFetch(target, { method: 'POST', body: payload });
    }

    const target: string = entityScope === EntityScope.CORE
      ? `${baseUrl}/core/${encodeURIComponent(initialData!.id)}`
      : `${baseUrl}/tenant/${encodeURIComponent(initialData!.id)}`;

    return authFetch(target, { method: 'PATCH', body: payload });
  };

  const onSubmitForm = useCallback(async (values: FormValues): Promise<void> => {
    try {
      const presignedUrls: VideoUploadDto | null = await createPresignedUrls(values);
      if (presignedUrls) {
        await uploadSelectedVideoFiles(presignedUrls, values);
      }
      
      const input: ExerciseRequest = buildInputDto(presignedUrls, values);
      const result: ExerciseDto = await submitData(input);
      onSubmit(result);
    } catch (error) {
      console.error(error);
    }
  }, [entityScope, initialData]);

  const onSubmitFormForRelaxationExercise = useCallback(async (values: FormValues): Promise<void> => {
    try {
      const presignedUrls: AudioUploadDto | null = await createPresignedUrlForRelaxationExercise(values);
      if (presignedUrls) {
        await uploadSelectedAudioFile(presignedUrls, values);
      }

      const input: ExerciseRequest = buildInputDtoForRelaxationExercise(presignedUrls, values);
      const result: ExerciseDto = await submitData(input);
      onSubmit(result);
    } catch (error) {
      console.error(error);
    }
  }, [entityScope, initialData]);

  useEffect(() => {
    return (): void => {
      Object.values(previewUrls)
        .filter((url: string | null): url is string => !!url)
        .forEach((url: string) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);


  const handleTypeChange = (setFieldValue: (f: keyof FormValues, v: any) => void) => (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const next: string = ev.target.value;
    setFieldValue('exerciseType', next);

    if (next === ExerciseType.RELAXATION) {
      setFieldValue('horizontalVideo', null);
      setFieldValue('verticalVideo', null);
      setFieldValue('horizontalThumbnail', null);
      setFieldValue('verticalThumbnail', null);
      setPreviewUrls((prev: PreviewUrls) => ({
        ...prev,
        horizontalVideo: null,
        verticalVideo: null,
        horizontalThumbnail: null,
        verticalThumbnail: null
      }));
    } else {
      setFieldValue('audioFile', null);
      setPreviewUrls((prev: PreviewUrls) => ({ ...prev, audioFile: null }));
    }
  };

  return (
    <div className="position-relative">
      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: FormValues) => {
          if (values.exerciseType === ExerciseType.RELAXATION) {
            onSubmitFormForRelaxationExercise(values);
          } else {
            onSubmitForm(values);
          }
        }}>
        {({
          values,
          touched,
          errors,
          handleSubmit,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          validateForm
        }: FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2">
            <Stack gap={3}>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group controlId="germanTitle">
                    <Form.Label>
                      {i18next.t('exerciseVideo.germanTitle')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="germanTitle"
                      value={values.germanTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.germanTitle && !!errors.germanTitle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.germanTitle}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group controlId="englishTitle">
                    <Form.Label>
                      {i18next.t('exerciseVideo.englishTitle')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="englishTitle"
                      value={values.englishTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.englishTitle && !!errors.englishTitle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.englishTitle}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group controlId="germanDescription">
                    <Form.Label>
                      {i18next.t('exerciseVideo.germanDescription')}
                    </Form.Label>
                    <LexicalEditor
                      value={values.germanDescription}
                      onChange={(value: string) => setFieldValue('germanDescription', value)}>
                    </LexicalEditor>
                    {touched.germanDescription && errors.germanDescription && (
                      <div className="invalid-feedback d-block">{errors.germanDescription}</div>
                    )}
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group controlId="englishDescription">
                    <Form.Label>
                      {i18next.t('exerciseVideo.englishDescription')}
                    </Form.Label>
                    <LexicalEditor
                      value={values.englishDescription}
                      onChange={(value: string) => setFieldValue('englishDescription', value)}>
                    </LexicalEditor>
                    {touched.englishDescription && errors.englishDescription && (
                      <div className="invalid-feedback d-block">{errors.englishDescription}</div>
                    )}
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <Form.Group controlId="exerciseType">
                  <Form.Label>
                    {i18next.t('exerciseVideo.exerciseType')}
                  </Form.Label>
                  <Form.Select
                    name="exerciseType"
                    className="select-xl"
                    value={values.exerciseType}
                    onChange={handleTypeChange(setFieldValue)}
                    onBlur={handleBlur}
                    isInvalid={touched.exerciseType && !!errors.exerciseType}
                    disabled={!isSuperAdmin}
                  >
                    {Object.entries(ExerciseType).map(([key, value]) => (
                      <option key={key} value={value}>{i18next.t(exerciseTypeLabels[value])}</option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.exerciseType}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              
              <div className="row">
                <Form.Group controlId="exerciseTags">
                  <Form.Label>{i18next.t('exerciseVideo.tags')}</Form.Label>
                  <Typeahead
                    id="tags-typeahead"
                    multiple
                    labelKey={(option: any) =>
                      `${i18next.language === 'de' ? option.de : option.en}`}
                    options={tags}
                    selected={values.tags}
                    onChange={(selected: any[]) => setFieldValue('tags', selected)}
                    clearButton>
                  </Typeahead>
                  {touched.tags && errors.tags && typeof errors.tags === 'string' && (
                    <div className="invalid-feedback d-block">{errors.tags}</div>
                  )}
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group controlId="isTrainingTimeBillable">
                  <Toggle
                    id="isTrainingTimeBillable"
                    label={i18next.t('exerciseVideo.isTrainingTimeBillable')}
                    checked={values.isTrainingTimeBillable}
                    onChange={(value: boolean) => {
                      setFieldValue('isTrainingTimeBillable', value);
                      setFieldTouched('isTrainingTimeBillable', true, false);
                    }}
                  />
                </Form.Group>
              </div>

              {isSuperAdmin && (
                <>
                  <hr className="my-2" />

                  {values.exerciseType === ExerciseType.RELAXATION ? (
                    <Row className="g-3">
                      <Col md={12}>
                        <div className="position-relative">
                          <DropZone
                            label={i18next.t('exerciseVideo.audioFile')}
                            accept="audio/mpeg,audio/*"
                            previewUrl={previewUrls.audioFile ?? existingUrls.audioFile}
                            disabled={isSubmitting}
                            onError={(message: string) => console.error(message)}
                            onFileSelect={(file: File): void => {
                              setFieldValue('audioFile', file);
                              setFieldTouched('audioFile', true, false);
                              setPreviewUrls((prev: PreviewUrls) => ({ ...prev, audioFile: URL.createObjectURL(file) }));
                            }}
                          />
                          <UploadOverlay {...uploadStates.audioFile} />
                          {touched.audioFile && errors.audioFile && (
                            <div className="invalid-feedback d-block">{errors.audioFile}</div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="position-relative">
                            <DropZone
                              label={i18next.t('exerciseVideo.horizontalVideo')}
                              accept="video/*"
                              previewUrl={previewUrls.horizontalVideo ?? existingUrls.horizontalVideo}
                              disabled={isSubmitting}
                              onError={(message: string) => console.error(message)}
                              onFileSelect={(file: File): void => {
                                setFieldValue('horizontalVideo', file);
                                setFieldTouched('horizontalVideo', true, false);
                                setPreviewUrls((prev: PreviewUrls) => ({ ...prev, horizontalVideo: URL.createObjectURL(file) }));
                              }}
                            />
                            <UploadOverlay {...uploadStates.horizontalVideo} />
                            {touched.horizontalVideo && errors.horizontalVideo && (
                              <div className="invalid-feedback d-block">{errors.horizontalVideo}</div>
                            )}
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="position-relative">
                            <DropZone
                              label={i18next.t('exerciseVideo.verticalVideo')}
                              accept="video/*"
                              previewUrl={previewUrls.verticalVideo ?? existingUrls.verticalVideo}
                              disabled={isSubmitting}
                              onError={(message: string) => console.error(message)}
                              onFileSelect={(file: File): void => {
                                setFieldValue('verticalVideo', file);
                                setFieldTouched('verticalVideo', true, false);
                                setPreviewUrls((prev: PreviewUrls) => ({ ...prev, verticalVideo: URL.createObjectURL(file) }));
                              }}
                            />
                            <UploadOverlay {...uploadStates.verticalVideo} />
                            {touched.verticalVideo && errors.verticalVideo && (
                              <div className="invalid-feedback d-block">{errors.verticalVideo}</div>
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="position-relative">
                            <DropZone
                              label={i18next.t('exerciseVideo.horizontalThumbnail')}
                              accept="image/*"
                              previewUrl={previewUrls.horizontalThumbnail ?? existingUrls.horizontalThumbnail}
                              disabled={isSubmitting}
                              onError={(message: string) => console.error(message)}
                              onFileSelect={(file: File): void => {
                                setFieldValue('horizontalThumbnail', file);
                                setFieldTouched('horizontalThumbnail', true, false);
                                setPreviewUrls((prev: PreviewUrls) => ({ ...prev, horizontalThumbnail: URL.createObjectURL(file) }));
                              }}
                            />
                            <UploadOverlay {...uploadStates.horizontalThumbnail} />
                            {touched.horizontalThumbnail && errors.horizontalThumbnail && (
                              <div className="invalid-feedback d-block">{errors.horizontalThumbnail}</div>
                            )}
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="position-relative">
                            <DropZone
                              label={i18next.t('exerciseVideo.verticalThumbnail')}
                              accept="image/*"
                              previewUrl={previewUrls.verticalThumbnail ?? existingUrls.verticalThumbnail}
                              disabled={isSubmitting}
                              onError={(message: string) => console.error(message)}
                              onFileSelect={(file: File): void => {
                                setFieldValue('verticalThumbnail', file);
                                setFieldTouched('verticalThumbnail', true, false);
                                setPreviewUrls((prev: PreviewUrls) => ({ ...prev, verticalThumbnail: URL.createObjectURL(file) }));
                              }}
                            />
                            <UploadOverlay {...uploadStates.verticalThumbnail} />
                            {touched.verticalThumbnail && errors.verticalThumbnail && (
                              <div className="invalid-feedback d-block">{errors.verticalThumbnail}</div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}

              <div className="d-flex justify-content-end gap-2 mt-2">
                <Button
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                  onClick={onCancel}
                  type="button">
                  {i18next.t('generic.cancel')}
                </Button>
                <Button
                  className="btn btn-primary"
                  disabled={isSubmitting}
                  type="submit"
                  onClick={async () => {
                    const validationErrors = await validateForm();
                    if (Object.keys(validationErrors).length) {
                      console.warn('Exercise form validation failed:', validationErrors);
                    }
                  }}>
                  {i18next.t(initialData ? 'generic.save' : 'generic.create')}
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ExerciseForm;
